import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Paper, Stack, Button, ButtonGroup } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import GoldenArrow from '../components/common/GoldenArrow';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, ICellRendererParams } from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';
import apiService from '../services/api.service';
import PRGlobalPicker from '../components/common/PRGlobalPicker';
import { transformPRForCopy } from '../utils/prCopyTransformer';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const toDDMMYYYY = (v: any): string => {
  const s = String(v || '').trim();
  if (!s) return '';
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[1]}-${slash[2]}-${slash[3]}`;
  const compact = s.match(/^(\d{8})$/);
  if (compact) return `${s.slice(6,8)}-${s.slice(4,6)}-${s.slice(0,4)}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
};

const InProgress: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const navigate = useNavigate();

  const handleCopyPR = useCallback(async (prId: number) => {
    try {
      const response = await apiService.getPR(prId);
      const prData = (response as any)?.data || response;
      const copiedData = transformPRForCopy(prData);

      navigate('/create-pr', {
        state: {
          copiedData,
          sourcePRId: prId
        }
      });
    } catch (error: any) {
      console.error('Failed to copy PR:', error);
    }
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [myRes, approverRes] = await Promise.all([
        apiService.getMyPRs(),
        apiService.getApproverPRs(),
      ]);
      const myList = (myRes as any)?.data || myRes || [];
      const apprList = (approverRes as any)?.data || approverRes || [];
      let list = [...myList, ...apprList];
      const seen = new Set<number>();
      list = list.filter((r: any) => { const id = Number(r.prid || r.PRID || 0); if (!id) return false; if (seen.has(id)) return false; seen.add(id); return true; });
      list = list.filter((r: any) => {
        const st = String(r.status || '').toLowerCase();
        const sapDocNum = r.sappodocnum || r.SAPPODocNum;
        const posted = st.includes('posted') || (sapDocNum && String(sapDocNum).trim() !== '') || Boolean((r as any).convertedtopo);
        const fullyApproved = st === 'approved';
        const isCompletedInbox = (r as any).inboxstatus === 'completed';
        const rejected = st === 'rejected';
        const pending = st.startsWith('pending');
        return !posted && !fullyApproved && !isCompletedInbox && !rejected && pending;
      });
      const withIds = list.map((r: any, i: number) => ({ id: r.prid ?? i, ...r }));
      setRows(withIds);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const renderPrNumber = (params: ICellRendererParams) => {
    const sapPO = params.data.sappodocnum || params.data.SAPPODocNum;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, whiteSpace: 'nowrap' }} noWrap>
            {params.data.prnumber || ''}
          </Typography>
          {sapPO && (
            <Typography component="span" sx={{ color: '#2E7D32', fontSize: '11px', ml: 1 }}>
              (SAP: {sapPO})
            </Typography>
          )}
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <GoldenArrow prId={Number(params.data.prid)} onAfterAction={load} />
        </Box>
      </Box>
    );
  };

  const renderPrDate = (params: ICellRendererParams) => {
    return (
      <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>
        {toDDMMYYYY(params.data.prdate)}
      </Typography>
    );
  };

  const renderRequester = (params: ICellRendererParams) => {
    return (
      <Typography sx={{ fontSize: '12px', fontWeight: 400 }} noWrap title={params.data.requestedbyname}>
        {params.data.requestedbyname}
      </Typography>
    );
  };

  const renderType = (params: ICellRendererParams) => {
    return (
      <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>
        {params.data.requisitiontype || ''}
      </Typography>
    );
  };

  const renderAllocationType = (params: ICellRendererParams) => {
    return (
      <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>
        {params.data.allocationtype || ''}
      </Typography>
    );
  };

  const renderAllocationCode = (params: ICellRendererParams) => {
    return (
      <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>
        {params.data.allocationcode || ''}
      </Typography>
    );
  };

  const renderStatus = (params: ICellRendererParams) => {
    const status = String(params.data.status || '').toLowerCase();
    const isPending = status.startsWith('pending');
    const isApproved = status === 'approved';
    const bgColor = isApproved ? '#E8F5E9' : isPending ? '#FFF3E0' : '#E3F2FD';
    const textColor = isApproved ? '#2E7D32' : isPending ? '#E65100' : '#1565C0';
    return (
      <Box sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: bgColor, width: '100%' }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 500, color: textColor }} noWrap>
          {params.data.status || ''}
        </Typography>
      </Box>
    );
  };

  const renderStep = (params: ICellRendererParams) => {
    const step = params.data.currentstep;
    return step ? (
      <Typography sx={{ fontSize: '12px', fontWeight: 400, textAlign: 'center', display: 'block' }}>
        {step}
      </Typography>
    ) : null;
  };

  const renderRequiredDate = (params: ICellRendererParams) => {
    return (
      <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>
        {toDDMMYYYY(params.data.requireddate)}
      </Typography>
    );
  };

  const renderAmount = (params: ICellRendererParams) => {
    return (
      <Typography sx={{ fontSize: '12px', fontWeight: 600, textAlign: 'right', display: 'block' }}>
        {Number(params.data.totalamount || 0).toFixed(2)} {params.data.currency || ''}
      </Typography>
    );
  };

  const renderActions = (params: ICellRendererParams) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <ButtonGroup sx={{ height: 24 }}>
          <Button
            size="small"
            sx={{ minWidth: 32, height: 24, fontSize: '11px', fontWeight: 500 }}
            onClick={() => window.dispatchEvent(new CustomEvent('pr-view-click', { detail: { prId: params.data.prid } }))}
          >
            View
          </Button>
          <Button
            size="small"
            sx={{ minWidth: 32, height: 24, fontSize: '11px', fontWeight: 500 }}
            onClick={() => handleCopyPR(params.data.prid)}
          >
            Copy
          </Button>
        </ButtonGroup>
      </Box>
    );
  };

  const [columnDefs] = useState([
    {
      field: 'prnumber',
      headerName: 'PR Number',
      width: 280,
      cellRenderer: renderPrNumber
    },
    {
      field: 'prdate',
      headerName: 'PR Date',
      width: 120,
      cellRenderer: renderPrDate
    },
    {
      field: 'requestedbyname',
      headerName: 'Requester',
      width: 160,
      cellRenderer: renderRequester
    },
    {
      field: 'requisitiontype',
      headerName: 'Type',
      width: 110,
      cellRenderer: renderType
    },
    {
      field: 'allocationtype',
      headerName: 'Allocation Type',
      width: 130,
      cellRenderer: renderAllocationType
    },
    {
      field: 'allocationcode',
      headerName: 'Allocation Code',
      width: 140,
      cellRenderer: renderAllocationCode
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      cellRenderer: renderStatus
    },
    {
      field: 'currentstep',
      headerName: 'Step',
      width: 80,
      cellRenderer: renderStep
    },
    {
      field: 'requireddate',
      headerName: 'Needed By',
      width: 120,
      cellRenderer: renderRequiredDate
    },
    {
      field: 'totalamount',
      headerName: 'Amount',
      width: 130,
      cellRenderer: renderAmount
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      suppressSizeToFit: true,
      cellRenderer: renderActions
    }
  ]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onUpdated = () => { void load(); };
    window.addEventListener('pr-updated', onUpdated as any);
    return () => { window.removeEventListener('pr-updated', onUpdated as any); };
  }, [load]);

  useEffect(() => {
    const onCompanyChanged = () => { void load(); };
    window.addEventListener('company-changed', onCompanyChanged as any);
    return () => { window.removeEventListener('company-changed', onCompanyChanged as any); };
  }, [load]);

  return (
    <>
      <Paper elevation={0} sx={{ p: 3, height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column', fontFamily: "'Inter', 'IBM Plex Sans', sans-serif" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" sx={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a' }}>In Progress</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" onClick={() => setPickerOpen(true)}>Search PR</Button>
            <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
          </Stack>
        </Stack>
        <Box sx={{ width: '100%', flexGrow: 1, minHeight: 0 }}>
          <div
            className="ag-theme-alpine"
            style={{
              height: '100%',
              width: '100%',
              border: '2px solid #F0AB00',
              fontFamily: "'Inter', 'IBM Plex Sans', sans-serif"
            }}
          >
            <AgGridReact
              rowData={rows}
              columnDefs={columnDefs}
              domLayout="normal"
              rowHeight={36}
              headerHeight={40}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
                flex: 0,
                minWidth: 80
              }}
              pagination={true}
              paginationPageSize={25}
              paginationPageSizeSelector={[25, 50, 100]}
              suppressPaginationPanel={false}
              getRowId={(params: any) => String(params.data.prid || params.data.id)}
              getRowStyle={(params: any) => ({
                backgroundColor: (params.node.rowIndex ?? 0) % 2 === 0 ? '#FFFFFF' : '#F8F8F8'
              })}
              theme="legacy"
              overlayLoadingTemplate='<span></span>'
              overlayNoRowsTemplate='<div style="padding: 20px; color: #666; font-family: Inter, sans-serif;">No records to display</div>'
              defaultColGroupDef={{
                headerClass: 'ag-header-cell'
              }}
            />
          </div>
        </Box>
        <PRGlobalPicker open={pickerOpen} onClose={() => setPickerOpen(false)} context="inprogress" />
      </Paper>
      <style dangerouslySetInnerHTML={{__html: `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');

        .ag-header-cell {
          font-family: 'Inter', 'IBM Plex Sans', sans-serif !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          color: #1a1a1a !important;
          background: #f5f5f5 !important;
          border-bottom: 2px solid #F0AB00 !important;
        }

        .ag-header-cell-label {
          font-family: 'Inter', 'IBM Plex Sans', sans-serif !important;
        }

        .ag-cell {
          font-family: 'Inter', 'IBM Plex Sans', sans-serif !important;
          font-size: 12px !important;
          font-weight: 400 !important;
          color: #333333 !important;
          padding: 8px 12px !important;
        }

        .ag-row:hover .ag-cell {
          background-color: #e3f2fd !important;
        }

        .ag-paging-panel {
          font-family: 'Inter', 'IBM Plex Sans', sans-serif !important;
          background: #f5f5f5 !important;
          border-top: 2px solid #F0AB00 !important;
          padding: 8px 12px !important;
        }

        .ag-paging-button {
          font-family: 'Inter', 'IBM Plex Sans', sans-serif !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          color: #0a6ed1 !important;
          background: #ffffff !important;
          border: 1px solid #e0e0e0 !important;
          border-radius: 4px !important;
          padding: 6px 12px !important;
        }

        .ag-paging-button:hover {
          background: #f0f0f0 !important;
          border-color: #0a6ed1 !important;
        }

        .ag-paging-button[disabled] {
          color: #cccccc !important;
          background: #f9f9f9 !important;
          border-color: #e0e0e0 !important;
        }
      `}} />
    </>
  );
};

export default InProgress;
